import * as React from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Modal, Box, Typography, Chip, Alert, Tooltip,
    FormControl, Autocomplete, TextField, ToggleButton
} from '@mui/material'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'

import { ErrorType, ActionType, LogLevel } from "../types.js"
import { getTranslation } from '../translations.js'

function Log(props) {
    const [currentLogs, setCurrentLogs] = React.useState([])
    const [lastError, setLastError] = React.useState(null) // { text, time }
    const { t, isOpen } = props;

    React.useEffect(() => {
        const logGrabber = msg => {
            let [err, action, obj] = JSON.parse(msg.data.toString())
            if (Number(action) !== ActionType.GetLogs || Number(err) !== ErrorType.Success) return

            const sorted = obj[0].splice(obj[1], obj[0].length - 1).concat(obj[0])

            // Sadece son hata logunu goster
            const now = Date.now()
            const newest = sorted[sorted.length - 1]
            if (newest && newest[0] === LogLevel.Error) {
                setLastError({ text: newest[1], time: now })
            }

            setCurrentLogs(sorted.filter(Boolean).map((obj, index) =>
                <div key={`${Date.now()}-${index}`} style={{
                    color: obj[0] === LogLevel.Error ? "#ff5555"
                        : obj[0] === LogLevel.Warn ? "#ffb86c"
                        : obj[0] === LogLevel.Success ? "#50fa7b"
                        : "#f8f8f2",
                    fontFamily: 'monospace', borderBottom: '1px solid #1e1e1e', padding: '1px 0'
                }}>
                    {obj[1]}
                </div>
            ).reverse())
        }
        props.ws.addEventListener("message", logGrabber)

        if (props.user && isOpen) {
            props.ws.send(JSON.stringify([ErrorType.Success, ActionType.GetLogs, props.user]));
        }

        return () => props.ws.removeEventListener("message", logGrabber)
    }, [props.ws, props.user, isOpen])

    return (
        <Paper onClick={e => e.stopPropagation()} sx={{ position: 'fixed', top: '2.5vh', left: '3.5vw', width: '93vw', height: '95vh', overflow: 'hidden', bgcolor: '#0a0a0a', color: '#f8f8f2', display: 'flex', flexDirection: 'column', borderRadius: 2, border: '1px solid #333' }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1a1a2e' }}>
                <Typography variant="h6" sx={{ color: '#90caf9', fontFamily: 'monospace', fontWeight: 'bold' }}>
                    {props.user ? `${props.user.name} - ${t("Gunlukler")}` : t("Sistem Gunlukleri")}
                </Typography>
                <Button variant="outlined" color="error" size="small" onClick={() => setCurrentLogs([])}>
                    {t("Clear Logs")}
                </Button>
            </Box>

            {/* Son Hata Bildirimi */}
            {lastError && (
                <Box onClick={e => e.stopPropagation()} sx={{ bgcolor: 'rgba(255,85,85,0.1)', borderBottom: '1px solid #ff5555', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: '#ff5555', fontWeight: 'bold', fontFamily: 'monospace', mr: 1 }}>
                            ⚠ SON HATA
                        </Typography>
                        <span style={{ color: '#6272a4', fontFamily: 'monospace', fontSize: '0.8rem', marginRight: '8px' }}>
                            {new Date(lastError.time).toLocaleTimeString('tr-TR')}
                        </span>
                        <span style={{ color: '#ff5555', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {lastError.text}
                        </span>
                    </Box>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setLastError(null) }} sx={{ color: '#ff5555', minWidth: 0, p: '0 6px', fontSize: '0.7rem' }}>
                        ✕
                    </Button>
                </Box>
            )}

            <div onClick={e => e.stopPropagation()} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 12px', backgroundColor: '#0a0a0a' }}>
                <Typography variant="body2" component="div" align='left' sx={{ fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.72rem', lineHeight: 1.45 }}>
                    {currentLogs}
                </Typography>
            </div>
        </Paper>)
}

export default function GGEUserTable(props) {
    const { language } = props;
    const t = (key) => getTranslation(language, key);

    const [openLogs, setOpenLogs] = React.useState(false)
    const [logUser, setLogUser] = React.useState(null)
    const [showActiveOnly, setShowActiveOnly] = React.useState(false)

    const handleLogClose = () => { setOpenLogs(false); setLogUser(null); }
    const handleLogOpen = () => setOpenLogs(true)
    const formatTroops = amount => {
        const value = Number(amount) || 0
        if (value >= 1000000) return `${(value / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} milyon`
        if (value >= 1000) return `${(value / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} bin`
        return value.toLocaleString('tr-TR')
    }

    const PlayerTable = () => {
        const troopAccounts = props.rows
            .map(row => ({ row, castles: props.usersStatus[row.id]?.castleTroops || [] }))
            .filter(account => account.castles.length > 0)

        return (
            <>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 25, 41, 0.5)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t("Name")}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t("Plugins")}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t("Status")}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t("Actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.rows.map((row, index) => {
                                const PlayerRow = () => {
                                const getEnabledPlugins = () => {
                                    let enabledPlugins = []
                                    Object.entries(row.plugins).forEach(([key, value]) => {
                                        if (value.state === true && key !== 'presets') {
                                            // Find plugin info from props.plugins to get the proper name
                                            const plugin = props.plugins.find(p => p.key === key);
                                            const displayName = plugin ? t(plugin.name) : t(key);
                                            enabledPlugins.push(displayName);
                                        }
                                    })
                                    return enabledPlugins
                                }
                                const [state, setState] = React.useState(row.state)
                                row.state = state
                                let status = props.usersStatus[row.id] ?? {}

                                const attackHits = status.attackStats?.currentHits ?? null;
                                return (
                                    <TableRow
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '&:hover': { bgcolor: 'rgba(144,202,249,0.04)' },
                                            transition: 'background 0.15s'
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Tooltip title={state ? t("Çalışıyor") : t("Durdu")} arrow>
                                                    <FiberManualRecordIcon sx={{ fontSize: 12, color: state ? '#4caf50' : '#555', flexShrink: 0 }} />
                                                </Tooltip>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">{row.name}</Typography>
                                                    <Typography variant="caption" color="gray">{row.server}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Typography variant="caption" color="text.secondary">
                                                {getEnabledPlugins().join(", ") || <span style={{color:'#555'}}>—</span>}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {status.level && <Chip label={`Lv ${status.level}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                                                {status.coin != null && <Chip label={`🪙 Altın: ${Number(status.coin).toLocaleString()}`} size="small" color="warning" sx={{ fontSize: '0.7rem' }} />}
                                                {status.rubies != null && <Chip label={`💎 Yakut: ${Number(status.rubies).toLocaleString()}`} size="small" color="error" sx={{ fontSize: '0.7rem' }} />}
                                                {attackHits != null && (
                                                    <Chip label={`⚔ ${attackHits} ${t("vurus")}`} size="small" sx={{ fontSize: '0.7rem', bgcolor: 'rgba(144,202,249,0.15)', color: '#90caf9', border: '1px solid rgba(144,202,249,0.3)' }} />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => { setLogUser(row); handleLogOpen() }}>{t("Logs")}</Button>
                                            <Button size="small" onClick={() => props.onSelectUser(row)}>{t("Settings")}</Button>
                                            <Button size="small" variant="contained" color={state ? "error" : "success"} onClick={() => {
                                                row.state = !state; props.ws.send(JSON.stringify([ErrorType.Success, ActionType.SetUser, row])); setState(!state)
                                            }} sx={{ ml: 1 }}>{state ? t("Stop") : t("Start")}</Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                                return <PlayerRow key={row.id} />
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {troopAccounts.length > 0 && (
                    <Paper sx={{ mt: 2, p: 1.75, bgcolor: 'rgba(10,25,41,0.72)', border: '1px solid rgba(144,202,249,0.18)', borderRadius: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1.25 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ color: '#90caf9', fontWeight: 700 }}>🛡️ Kalelerdeki Askerler</Typography>
                                <Typography variant="caption" color="text.secondary">Son kale listesindeki hazır birlik sayıları</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#607d8b' }}>DCL ile otomatik güncellenir</Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 0.9 }}>
                            {troopAccounts.flatMap(({ row, castles }) => castles.map(castle => (
                                <Box key={`${row.id}-${castle.kingdomID}-${castle.areaID}`} sx={{ p: 1, borderRadius: 1.25, bgcolor: 'rgba(144,202,249,0.045)', border: '1px solid rgba(144,202,249,0.11)' }}>
                                    {troopAccounts.length > 1 && <Typography variant="caption" sx={{ display: 'block', color: '#78909c', mb: 0.25 }}>{row.name}</Typography>}
                                    <Typography variant="caption" noWrap title={castle.name} sx={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{castle.name}</Typography>
                                    <Typography variant="subtitle1" sx={{ mt: 0.15, color: '#e3f2fd', fontWeight: 700 }}>{formatTroops(castle.troops)}</Typography>
                                    <Typography variant="caption" color="text.secondary">hazır asker</Typography>
                                </Box>
                            )))}
                        </Box>
                    </Paper>
                )}
            </>
        )
    }

    return (
        <>
            <Modal open={openLogs} onClose={() => { props.ws.send(JSON.stringify([ErrorType.Success, ActionType.GetLogs, undefined])); handleLogClose() }}>
                <Log ws={props.ws} t={t} user={logUser} isOpen={openLogs} />
            </Modal>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="primary">{t("Bot Durumu")}</Typography>
            </Box>

            {props.isAdmin && (() => {
                const uniqueAccounts = Array.from(new Map(props.allAccounts?.map(a => [a.userId, a]) ?? []).values());
                const now = new Date();
                const subscriptionActive = uniqueAccounts.filter(a => a.subscriptionEnd && new Date(a.subscriptionEnd) > now);
                const filteredAccounts = showActiveOnly ? subscriptionActive.filter(a => a.isActive) : subscriptionActive;
                const selectedAcc = props.adminViewingUser ? (uniqueAccounts.find(a => a.userId === props.adminViewingUser) ?? null) : null;
                return (
                    <Box sx={{ mb: 3, p: 2, border: '1px solid rgba(144,202,249,0.2)', borderRadius: 2, bgcolor: 'rgba(144,202,249,0.05)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <AdminPanelSettingsIcon sx={{ color: '#90caf9', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#90caf9', fontWeight: 'bold' }}>Admin Paneli</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Autocomplete
                                size="small"
                                sx={{ minWidth: 340 }}
                                options={filteredAccounts}
                                getOptionLabel={(opt) => opt ? `${opt.ownerName} (${opt.username} — S${opt.server})` : ''}
                                isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
                                value={selectedAcc}
                                onChange={(_, newVal) => {
                                    props.onAdminSelectUser(newVal ?? null);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Hesap Seç" placeholder="Ara..." />
                                )}
                                noOptionsText="Sonuç bulunamadı"
                                clearText="Kendi Hesabım"
                            />
                            <ToggleButton
                                value="activeOnly"
                                selected={showActiveOnly}
                                onChange={() => setShowActiveOnly(prev => !prev)}
                                size="small"
                                sx={{
                                    color: showActiveOnly ? '#4caf50' : 'rgba(255,255,255,0.5)',
                                    borderColor: showActiveOnly ? '#4caf50' : 'rgba(255,255,255,0.2)',
                                    '&.Mui-selected': { bgcolor: 'rgba(76,175,80,0.15)', color: '#4caf50', borderColor: '#4caf50' },
                                    fontSize: '0.75rem',
                                    px: 1.5,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Sadece Aktifler
                            </ToggleButton>
                        </Box>
                    </Box>
                );
            })()}

            {!props.isAdmin && props.linkedAccounts?.length > 1 && (() => {
                const selectedAccount = props.linkedAccounts.find(
                    account => Number(account.userId) === Number(props.linkedViewingUser)
                ) ?? props.linkedAccounts.find(account => account.isOwn) ?? null;
                return (
                    <Box sx={{ mb: 3, p: 2, border: '1px solid rgba(210,153,34,0.25)', borderRadius: 2, bgcolor: 'rgba(210,153,34,0.06)' }}>
                        <Typography variant="body2" sx={{ color: '#d29922', fontWeight: 'bold', mb: 1.5 }}>
                            Bağlı Kaleler
                        </Typography>
                        <Autocomplete
                            size="small"
                            sx={{ maxWidth: 460 }}
                            options={props.linkedAccounts}
                            getOptionLabel={(option) => option
                                ? `${option.ownerName} (${option.username} — S${option.server})${option.isOwn ? ' — Kendi hesabım' : ''}`
                                : ''}
                            isOptionEqualToValue={(option, value) => Number(option.userId) === Number(value.userId)}
                            value={selectedAccount}
                            onChange={(_, newValue) => {
                                if (newValue) props.onLinkedSelectUser(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Yönetilecek Kaleyi Seç" placeholder="Kale ara..." />
                            )}
                            disableClearable
                            noOptionsText="Bağlı kale bulunamadı"
                        />
                    </Box>
                );
            })()}

            {(() => {
                const subDate = props.rows[0]?.subscriptionEndDate;
                if (!subDate) return null;
                const end = new Date(subDate);
                const now = new Date();
                const diffMs = end - now;
                const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                const severity = diffDays <= 3 ? "error" : diffDays <= 7 ? "warning" : "info";
                const dateStr = end.toLocaleDateString('tr-TR');
                return (
                    <Alert severity={severity} sx={{ mb: 2 }}>
                        {diffDays > 0
                            ? `${t("Abonelik")}: ${diffDays} ${t("gun kaldi")} (${dateStr})`
                            : t("Aboneliginiz sona erdi!")}
                    </Alert>
                );
            })()}

            {props.rows.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, color: 'gray' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Henüz bot yok</Typography>
                    <Typography variant="body2">Sol menüden bir bot seçin veya yönetici panelinden bot ekleyin.</Typography>
                </Box>
            ) : (
                <PlayerTable />
            )}
        </>
    )
}
